<template>
  <article class="slo-cart-product">
    <div class="slo-cart-product--wrapper">
      <figure class="slo-cart-product--image" v-if="hasSlot('image')">
        <a :href="`/sku/${sku}`" @click="closeMiniCart">
          <slot name="image" />
        </a>
      </figure>
      <div class="slo-cart-product--content-wrapper">
        <div class="slo-cart-product--content">
          <div class="slo-cart-product--header">
            <h3 class="slo-cart-product--title">
              <a :href="`/sku/${sku}`" @click="closeMiniCart" class="c-gray-900 hover:c-blue-700">
                {{ title }}
              </a>
            </h3>
            <div class="slo-cart-product--removal" v-if="!isUpSell && includeRemoveButton">
              <button @click="handleRemoveProduct">
                {{ 'Remove' | t }}
              </button>
            </div>
          </div>
          <p class="slo-cart-product--description">{{ description }}</p>
          <p class="slo-cart-product--specs">
            <span>{{ 'Code' | t }} {{ sku }}</span>
            <span v-if="productType">| {{ productType }}</span>
          </p>
          <SloProductMeta v-if="hasProductMeta" :product="productMeta" />
        </div>
      </div>
      <div v-if="!isRestrictedProduct" class="slo-cart-product--mobile-restriction-messages">
        <SloProductMeta :product="{restrictedMessage}" />
      </div>
      <div :class="{
        'slo-cart-product--price': true,
        'slo-cart-product--is-subscription': subscriptionFrequency && !isFullCart
      }">
        <template v-if="isUpSell">
          <SloAddToCart
            :sku="sku"
            maxWidth="lg"
            list-name="cart_upsell"
          >
            <SloProductMeta :product="productMetaWithPrice">
              <span class="unit-of-measure">{{ uom }}</span>
            </SloProductMeta>
          </SloAddToCart>
        </template>
        <template v-else-if="isCheckout">
          <div class="flex items-center flex-wrap gap-md w-100 sm:justify-between">
            <SloProductMeta :product="productMetaWithPrice">
              <span class="unit-of-measure">{{ uom }}</span>
            </SloProductMeta>
            <span class="checkout-quantity">Qty. {{ quantity }}</span>
            <span>{{ totalPrice }}</span>
          </div>
          <div v-if="subscriptionFrequency" class="flex flex-col items-start gap-xs">
            <p class="m-0">{{ 'Frequency' | t }}: {{ subscriptionFrequencyLabel }}</p>
            <p class="m-0 c-blue-400">
              <SloIcon name="ArrowsRotate" />
              <span class="text-sm">{{ 'subscription' | t }}</span>
            </p>
          </div>
        </template>
        <template v-else-if="!isFullCart">
          <div class="flex items-center justify-between flex-wrap gap-x-md gap-y-xs w-100">
            <SloProductMeta :product="productMetaWithPrice">
              <span class="unit-of-measure">{{ uom }}</span>
            </SloProductMeta>
            <span v-if="subscriptionFrequency" class="checkout-quantity">Qty. {{ quantity }}</span>
            <SloNumberInput
              v-else
              :key="`cart-product-quantity-${sku}`"
              :initialValue="quantity"
              :min="1"
              :min-error-text="'You must add a quantity of at least one for an item.' | t"
              :max="999"
              :max-error-text="'You can only order a maximum of 999 quantity for an item.'| t"
              @valueUpdate="$emit('quantityChange', $event)"
            />
            <span>{{ totalPrice }}</span>
          </div>
          <div v-if="subscriptionFrequency" class="flex flex-col items-start gap-xs">
            <p class="m-0">{{ 'Frequency' }}: {{ subscriptionFrequencyLabel }}</p>
            <p class="m-0 c-blue-400">
              <SloIcon name="ArrowsRotate" />
              <span class="text-sm">{{ 'subscription' | t }}</span>
            </p>
          </div>
        </template>
        <template v-else>
          <SloProductMeta :product="productMetaWithPrice">
            <span class="unit-of-measure">{{ uom }}</span>
            <span v-if="subscriptionFrequency" class="fw-medium c-green-400">
              ({{ subscriptionPercentageDiscount }}% {{ 'savings' | t }})
            </span>
            <div v-if="subscriptionFrequency" class="flex items-center gap-xs c-blue-400">
              <SloIcon name="ArrowsRotate" />
              <span class="text-sm fw-strong">{{ 'subscription' | t }}</span>
            </div>
          </SloProductMeta>
          <SloQuantityInput
            :quantity="quantity"
            :min="minOrderQuantity"
            @updated="$emit('quantityChange', $event)"
          />
          <SloField
            v-if="subscriptionFrequency && subscriptionFrequencies.length"
            class="m-0"
          >
            <SloSelect
              v-model="frequency"
              :placeholder="'Select frequency' | t"
              @input="$emit('frequencyChange', $event)"
              name="subscription_frequency"
            >
              <option
                v-for="frequency in subscriptionFrequencies"
                :key="`frequency-${frequency.code}`"
                :value="frequency.code"
                v-html="frequency.name"
              />
            </SloSelect>
          </SloField>
          <span>{{ totalPrice }}</span>
        </template>
      </div>
      <div
        v-if="isRestrictedProduct && shouldDisplayProductActions"
        class="slo-cart-product--restriction-actions flex items-center gap-sm"
      >
        <SloProductMeta :product="{restrictedMessage}">
          <template v-slot:restricted-messages-actions>
            <div class="slo-cart-product--restriction-actions-inner flex flex-wrap items-center gap-md">
              <SloButton :href="`/sku/${sku}?removeSkuFromCart=1`">
                {{ 'Update' | t }}
              </SloButton>
              <SloButton fill="outline" @click.stop="handleRemoveProduct">
                {{ 'Remove' | t }}
              </SloButton>
            </div>
          </template>
        </SloProductMeta>
      </div>
    </div>
    <div
      v-if="!isReturnable"
      class="flex items-center gap-sm mt-md mb-0 px-lg text-base fw-strong"
    >
      <span class="flex items-center justify-center c-orange-500">
        <SloIcon name="TriangleExclamationSolid" />
      </span>
      {{ 'This product cannot be returned once purchased' | t }}
    </div>
    <div
      v-if="!isCancellable"
      class="flex items-center gap-sm mt-md mb-0 px-lg text-base fw-strong"
    >
      <span class="flex items-center justify-center c-orange-500">
        <SloIcon name="TriangleExclamationSolid" />
      </span>
      {{ 'This product cannot be cancelled from your order once purchased' | t }}
    </div>
  </article>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import t from '../../main/i18n';
import {tagManagerTrack} from '../../main/utils';
import SloIcon from '../../../lib/components/SloIcon.vue';
import SloNumberInput from '../../../lib/components/SloNumberInput.vue';
import SloProductImage from '../../../lib/components/SloProductImage.vue';
import SloProductMeta from '../../../lib/components/SloProductMeta.vue';
import SloAddToCart from '../../../lib/components/SloAddToCart.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloField from '../../../lib/components/SloField.vue';
import SloSelect from '../../../lib/components/SloSelect.vue';
import SloQuantityInput from '../../../lib/components/SloQuantityInput.vue';

export default {
  name: 'CartProduct',
  components: {
    SloIcon,
    SloNumberInput,
    SloProductImage,
    SloProductMeta,
    SloAddToCart,
    SloButton,
    SloField,
    SloSelect,
    SloQuantityInput
  },
  props: {
    entryNumber: {type: Number},
    quantity: {type: Number},
    priceEach: {type: String},
    priceEachSubscription: {type: String},
    totalPrice: {type: String},
    estimatedShipDate: {type: String},
    minOrderQuantity: {type: String|Number, default: 1},
    restrictedMessage: {type: Array},
    shouldDisplayProductActions: {type: Boolean, default: false},
    shouldDisplaySubscriptionInput: {type: Boolean, default: false},
    isUpSell: {type: Boolean},
    isCheckout: {type: Boolean},
    isFullCart: {type: Boolean, default: false},
    isReturnable: {type: Boolean, default: true},
    isCancellable: {type: Boolean, default: true},
    title: {type: String},
    description: {type: String},
    sku: {type: String},
    productType: {type: String},
    uom: {type: String},
    subscriptionFrequencies: {type: Array, default: () => []},
    subscriptionFrequency: {type: String},
    subscriptionPercentageDiscount: {type: String|Number},
    includeRemoveButton: {type: Boolean, default: true}
  },
  filters: {
    t
  },
  data() {
    return {
      frequency: null
    };
  },
  computed: {
    ...mapGetters('auth', ['user']),
    ...mapGetters('marketplace', ['cartId']),
    isRestrictedProduct() {
      return Boolean(this.restrictedMessage && this.restrictedMessage.length > 0);
    },
    hasProductMeta() {
      return Boolean(
        this.estimatedShipDate || (this.restrictedMessage && !this.isRestrictedProduct)
      );
    },
    productMeta() {
      return {
        estimatedShipDate: this.estimatedShipDate,
        restrictedMessage: this.restrictedMessage
      };
    },
    productMetaWithPrice() {
      return {
        estimatedShipDate: null,
        price: {formattedValue: this.priceEachSubscription ?? this.priceEach},
        restrictedMessage: null
      };
    },
    subscriptionFrequencyLabel() {
      const subscriptionFrequencyMatch = this.subscriptionFrequencies.find(
        frequency => frequency.code === this.subscriptionFrequency
      );

      return subscriptionFrequencyMatch?.name || this.subscriptionFrequency;
    }
  },
  mounted() {
    this.frequency = this.subscriptionFrequency;
  },
  methods: {
    ...mapActions('marketplace', ['closeMiniCart']),
    hasSlot(name = 'default') {
      return Boolean(this.$slots[name] || this.$scopedSlots[name]);
    },
    handleRemoveProduct() {
      tagManagerTrack('remove_from_cart', {
        user: this.user?.accountNumber || 'guest',
        cartId: this.cartId,
        item_id: this.sku,
        item_name: this.title
      });

      this.$emit('removeProduct');
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../scss/system';

.slo-cart-product {
  position: relative;
  padding-block: 1.6rem;

  :deep(.slo-price-info) {
    position: relative;

    .price-info--shipping-date {
      margin-block: 0;
    }
    .price-info--price-wrapper {
      margin-block: 0;

      .price-info--price {
        @include text('base', 'strong');
      }
    }
  }

  &.up-sell {
    width: 100%;
    min-width: 100%;
    padding-inline: 2.4rem;

    @media (min-width: 768px) {
      padding-inline: 6.4rem;
    }

    & > div {
      padding: 0;
    }

    .slo-cart-product--price {
      grid-column: 1/5;
      flex-wrap: wrap;
    }

    :deep(.slo-add-to-cart--wrapper) {
      flex-direction: row;
      flex-wrap: wrap;

      .slo-price-info {
        flex: 1 0 100%;
      }

      .slo-button {
        flex: 1;
      }

      @media (min-width: 480px) {
        > *, .slo-price-info {
          flex: 1;
        }
      }
    }
  }

  &:not(.up-sell) {
    border-bottom: 1px solid $color-gray-400;

    .slo-cart-product--price {
      grid-row-start: 3;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: start;
      flex-wrap: wrap;
      gap: 1.2rem;
      justify-self: end;

      @media (min-width: 768px) {
        grid-column-start: 2;
        grid-row-start: 2;
        flex-direction: row;
        width: 100%;
      }

      :deep(.slo-price-info) {
        flex: 1 0 100%;

        @media (min-width: 480px) {
          flex: none;
        }
      }
    }

    .slo-cart-product--is-subscription {
      gap: 0.4rem;

      @media (min-width: 768px) {
        flex-direction: column;
        align-items: start;
      }
    }
  }

  &--price {
    grid-column: 2 / 5;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: start;
    gap: 1.2rem;

    @media (min-width: 480px) {
      grid-column: 1 / 5;
      justify-self: end;
      flex-direction: row;
      align-items: center;
      width: max-content;
      margin-block-start: 0;
    }

    span {
      @include text('base', 'strong');
    }

    .unit-of-measure, .checkout-quantity {
      font-weight: 400;
    }

    .unit-of-measure {
      text-transform: lowercase;
    }

    :deep(.slo-number-input) {
      max-width: 115px;
    }
  }

  &--wrapper {
    position: relative;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: 1fr auto;
    gap: 1.6rem;
    padding-inline: 2.4rem;
  }

  &--image {
    grid-column: 1 / 2;
    min-width: 90px;
    max-width: 90px;

    img {
      aspect-ratio: 1;
      height: 100%;
      width: 100%;
      object-fit: contain;
    }

    button {
      padding: 0;
      border: none;
      background-color: transparent;
    }
  }

  &--content-wrapper {
    grid-column: 2 / 5;
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.4rem;
    width: 100%;

    p {
      margin: 0;
    }

    :deep(.restriction-messages) {
      display: none;

      @media (min-width: 768px) {
        display: block;
      }
    }
  }

  &--header {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 0.4rem;
    width: 100%;
  }

  &--removal button {
    @include text('sm');
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background-color: transparent;
    padding: 0;
    color: $color-gray-600;

    &:hover {
      color: $color-gray-800;
    }
  }

  &--content {
    display: flex;
    flex-direction: column;
    align-items: start;
    justify-content: start;
    gap: 0.4rem;
  }

  &--title {
    @include text('base', 'strong');
  }

  &--description {
    @include text('base');
    color: $color-gray-800;
    max-width: 36ch;
  }

  &--specs {
    @include text('sm');
    color: $color-gray-600;

    span {
      color: $color-gray-800;
    }
  }

  &--mobile-restriction-messages {
    grid-column: 1 / -1;
    @media (min-width: 768px) {
      display: none;
    }
  }

  &--restriction-actions {
    grid-row: 4;
    grid-column: 1 / -1;

    @media (min-width: 768px) {
      grid-column: 2 / -1;
    }

    :deep(.restriction-message) {
      margin-block: 0;
    }
  }

  &--restriction-actions-inner {
    grid-column: 2 / -1;
  }
}
</style>
